import { ClubName } from 'app/components/club-name';
import { showClubName } from 'app/components/club-name.utils';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormatDurationShort } from 'app/lib/date';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { Placeholder, PlaceholderLine, PlaceholderMedia, ShineOverlay } from 'app/overrides/rn-placeholder';
import { useTranslation } from 'react-i18next';
import { Pressable, type ViewProps } from 'react-native';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import type { Listing } from 'app/types/listing';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { AmbassadorMedal } from 'app/design/icons/ambassador-medal';
import { SuperSharerMedal } from 'app/design/icons/super-sharer-medal';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useAmbassador } from 'app/hooks/use-ambassador';
const theme = createDSTheme();

type PosterSectionProps = Listing & {
  city?: string;
  hideLocation?: boolean;
  hideGroup?: boolean;
  onHero?: boolean;
  partialPostalCode?: string;
};

function PosterBadge({
  ambassadorEnabled,
  isAmbassador,
  isSuper,
  onHero,
}: {
  ambassadorEnabled: boolean;
  isAmbassador?: boolean | null;
  isSuper?: boolean | null;
  onHero: boolean;
}) {
  if (ambassadorEnabled && isAmbassador) {
    const size = onHero ? 32 : 48;
    return (
      <AmbassadorMedal
        width={size}
        height={size * (60 / 57)}
        style={{
          position: 'absolute',
          bottom: onHero ? -10 : -theme.spacing['spacing.general.x4'],
          right: onHero ? -4 : -theme.spacing['spacing.general.x2'],
        }}
      />
    );
  }

  if (!isSuper) return null;

  const size = onHero ? 34 : 50;
  return (
    <SuperSharerMedal
      width={size}
      height={size * (69 / 57)}
      style={{
        position: 'absolute',
        bottom: onHero ? -theme.spacing['spacing.general.x4'] : -theme.spacing['spacing.general.x6'],
        right: -theme.spacing['spacing.general.x2'],
      }}
    />
  );
}

function PosterAvatar({
  ambassadorEnabled,
  isAmbassador,
  isSuper,
  onHero,
  pictureUrl,
}: {
  ambassadorEnabled: boolean;
  isAmbassador?: boolean | null;
  isSuper?: boolean | null;
  onHero: boolean;
  pictureUrl?: string;
}) {
  const avatarSize = onHero ? 40 : 64;

  return (
    <Row>
      <PCImage
        aspectRatio={1}
        width={avatarSize}
        source={{ uri: pictureUrl }}
        style={{
          borderRadius: avatarSize / 2,
          ...(onHero ? { borderColor: 'rgba(255,255,255,0.9)', borderWidth: 1.5 } : {}),
        }}
        useOwnCache
      />
      <PosterBadge
        ambassadorEnabled={ambassadorEnabled}
        isAmbassador={isAmbassador}
        isSuper={isSuper}
        onHero={onHero}
      />
    </Row>
  );
}

function PosterStatus({
  isUnavailable,
  lastSeenAt,
  onHero,
}: {
  isUnavailable: boolean;
  lastSeenAt?: string | Date | null;
  onHero: boolean;
}) {
  const { t } = useTranslation('one-listing');
  let status = t('owner.member');
  if (isUnavailable) status = t('currentlyUnavailable', { ns: 'neighbour-profile' });
  else if (lastSeenAt) status = t('owner.lastActive', { ago: localeAwareFormatDurationShort(new Date(lastSeenAt)) });

  const statusColor = isUnavailable
    ? onHero
      ? 'rgba(255,255,255,0.72)'
      : theme.colors['text.placeholder']
    : theme.colors['text.positive'];

  return (
    <Row alignItems="center" flexWrap="wrap" gap="spacing.general.x1">
      <Column style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: statusColor }} />
      <Text
        color={onHero ? 'surface.default' : 'text.secondary'}
        numberOfLines={1}
        style={onHero ? { opacity: 0.84 } : undefined}
        variant="bodySmall"
      >
        {status}
      </Text>
    </Row>
  );
}

function PosterIdentity({
  isUnavailable,
  lastSeenAt,
  locationLabel,
  onHero,
  onPress,
  ownerName,
}: {
  isUnavailable: boolean;
  lastSeenAt?: string | Date | null;
  locationLabel?: string;
  onHero: boolean;
  onPress: () => void;
  ownerName: string;
}) {
  const { t } = useTranslation('one-listing');

  return (
    <Pressable
      accessibilityLabel={t('owner.openProfile', { name: ownerName })}
      accessibilityRole="button"
      onPress={onPress}
      style={{ flex: 1 }}
    >
      <Row alignItems="center" gap="spacing.general.x2">
        <Column flex={1} gap="spacing.general.x1">
          <Text
            color={onHero ? 'surface.default' : 'text.primary'}
            fontWeight="700"
            numberOfLines={1}
            variant="bodyLarge"
          >
            {ownerName}
          </Text>
          <PosterStatus isUnavailable={isUnavailable} lastSeenAt={lastSeenAt} onHero={onHero} />
          {locationLabel ? (
            <Text
              color={onHero ? 'surface.default' : 'text.secondary'}
              numberOfLines={1}
              style={onHero ? { opacity: 0.84 } : undefined}
              variant="bodySmall"
            >
              {locationLabel}
            </Text>
          ) : null}
        </Column>
        {onHero ? null : (
          <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['icon.tertiary']} />
        )}
      </Row>
    </Pressable>
  );
}

export function PosterSection({
  id: listingId,
  type: listingType,
  user,
  groups,
  distance,
  city,
  partialPostalCode,
  hideLocation,
  isCurrentlyUnavailable,
  hideGroup = false,
  onHero = false,
}: PosterSectionProps) {
  const { formatDistanceWithoutText } = useDistanceUnit();
  const navigation = useNavigation();
  const authorizeProfile = useAuthorize('member-profile');
  const ambassadorEnabled = useAmbassador().isEnabled;

  const isUnavailable = isCurrentlyUnavailable ?? false;
  const userProfilePictureUrl = getImageVariantUrl(user.profile.picture, 'mini');

  const onPressDetails = () => {
    return authorizeProfile(() => navigateToUserProfile(navigation, user.id, listingId, listingType));
  };

  const showClub = !hideGroup && showClubName(groups);
  const ownerName = [user.profile.firstName, user.profile.lastName].filter(Boolean).join(' ');
  const locationLabel =
    distance === undefined || hideLocation
      ? undefined
      : [city && city !== 'null' ? city : partialPostalCode, formatDistanceWithoutText(distance)]
          .filter(Boolean)
          .join(' · ');

  return (
    <Column
      gap="spacing.general.x4"
      style={
        showClub && !onHero
          ? {
              padding: theme.spacing['spacing.general.x3'],
              borderRadius: theme.borderRadii['radius.md'],
              borderWidth: 1,
              borderColor: theme.colors['surface.disabled'],
              backgroundColor: theme.colors['surface.tertiary-neutral'],
            }
          : undefined
      }
    >
      <Row alignItems="center" gap="spacing.general.x4">
        <PosterAvatar
          ambassadorEnabled={ambassadorEnabled}
          isAmbassador={user.isAmbassador}
          isSuper={user.isSuper}
          onHero={onHero}
          pictureUrl={userProfilePictureUrl}
        />
        <PosterIdentity
          isUnavailable={isUnavailable}
          lastSeenAt={user.lastSeenAt}
          locationLabel={locationLabel}
          onHero={onHero}
          onPress={onPressDetails}
          ownerName={ownerName}
        />
      </Row>

      {showClub ? <ClubName asTags groups={groups} /> : null}
    </Column>
  );
}

PosterSection.Skeleton = function PosterSectionSkeleton() {
  const leftElement = (props: ViewProps) => (
    <PlaceholderMedia
      style={[{ height: 100, width: 100, borderRadius: theme.borderRadii['radius.full'] }, props.style]}
    />
  );
  return (
    <Placeholder Animation={ShineOverlay} Left={leftElement}>
      <PlaceholderLine style={{ width: 140, marginBottom: 20 }} />
      <PlaceholderLine style={{ width: 50, marginBottom: 40 }} />

      <PlaceholderLine style={{ width: 120, height: 30, borderRadius: theme.borderRadii['radius.lg'] }} />
    </Placeholder>
  );
};
